import { FC, useState } from 'react';
import {
  startOfDay,
  isSameMonth,
  isSameYear,
  isSameDay,
  format,
  isBefore,
} from 'date-fns';
import {
  styled,
  Chip,
  Stack,
  FormHelperText,
  Button,
  Box as MuiBox,
  useTheme,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { DatePicker, HighlightInfo, SpecialDate } from '../date-picker/DatePicker';

export interface DateRange {
  start: Date;
  end: Date;
}

interface PropTypes {
  errorMessage?: string;
  dateRanges: DateRange[];
  onCancel?: () => void;
  specialDates?: SpecialDate[];
  currentDate?: Date;
  onSave?: (selectedRanges: DateRange[]) => Promise<void>;
}

export const MultiDateRangePicker: FC<PropTypes> = ({
  errorMessage,
  dateRanges,
  currentDate,
  onCancel,
  specialDates = [],
  onSave,
}) => {
  const theme = useTheme();
  const [selectedRanges, setSelectedRanges] = useState<DateRange[]>(dateRanges);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [selectingRange, setSelectingRange] = useState<DateRange | null>(null);
  const [currentMonth, setCurrentMonth] = useState(currentDate || new Date());

  const onChange = (value: Date | null) => {
    if (!value) {
      return;
    }

    const date = startOfDay(value);

    if (!selectingRange) {
      setSelectingRange({ start: date, end: date });
      return;
    }

    if (!isSameDay(selectingRange.start, selectingRange.end)) {
      setSelectingRange({ start: date, end: date });
      return;
    }

    if (isBefore(date, selectingRange.start)) {
      // If selected date is before start date, swap them
      const newRange = { start: date, end: selectingRange.start };
      setSelectedRanges([...selectedRanges, newRange]);
      setSelectingRange(null);
    } else {
      // Normal case: end date is after start date
      const newRange = { start: selectingRange.start, end: date };
      setSelectedRanges([...selectedRanges, newRange]);
      setSelectingRange(null);
    }
  };

  const cancelSelection = () => {
    setSelectingRange(null);
    onCancel?.();
  };

  const removeRange = (rangeToRemove: DateRange) => {
    const newRanges = selectedRanges.filter(
      (range) =>
        !isSameDay(range.start, rangeToRemove.start) ||
        !isSameDay(range.end, rangeToRemove.end),
    );
    setSelectedRanges(newRanges);
  };

  const isDaySelected = (day: Date) => {
    // Only return true for start and end dates, not dates within the range
    const allRanges = [...selectedRanges];
    if (selectingRange) {
      allRanges.push(selectingRange);
    }

    return allRanges.some(
      (range) => isSameDay(range.start, day) || isSameDay(range.end, day),
    );
  };

  const getNextMonth = (date: Date) => {
    const nextMonth = new Date(date);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    return nextMonth;
  };

  const getRangeHighlights = (): HighlightInfo[] => {
    const allRanges = [...selectedRanges];
    if (selectingRange) {
      if (hoveredDate) {
        // Ensure start is always before end to avoid invalid intervals
        const [rangeStart, rangeEnd] = [selectingRange.start, hoveredDate].sort(
          (a, b) => a.getTime() - b.getTime(),
        );
        allRanges.push({
          start: rangeStart,
          end: rangeEnd,
        });
      } else {
        allRanges.push(selectingRange);
      }
    }

    return allRanges.map((range) => {
      const daysDiff = Math.ceil(
        (range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60 * 24),
      );
      return {
        date: range.start,
        softHighlightDuration: daysDiff,
        heavyHighlightDuration: 0, // No heavy highlight for range days
      };
    });
  };

  return (
    <Stack direction="column" spacing={2}>
      <MuiBox sx={{ mt: 2 }} maxWidth={700}>
        {selectedRanges.map((range) => {
          return (
            <RangePill
              key={`${range.start.getTime()}-${range.end.getTime()}`}
              title={formatDateRangeString(range.start, range.end)}
              onRemove={() => removeRange(range)}
            />
          );
        })}
      </MuiBox>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" justifyContent="space-between" spacing={3}>
          <MuiBox>
            <DatePicker
              isDaySelected={isDaySelected}
              specialDates={specialDates}
              highlights={getRangeHighlights()}
              onChange={onChange}
              rangeBackground={theme.palette.principal.grey30}
              value={currentMonth}
              onMonthChange={(date: Date) => setCurrentMonth(date)}
              onYearChange={(date: Date) => setCurrentMonth(date)}
              onMouseEnter={(date: Date) => {
                setHoveredDate(date);
              }}
            />
          </MuiBox>
          <MuiBox>
            <DatePicker
              isDaySelected={isDaySelected}
              specialDates={specialDates}
              highlights={getRangeHighlights()}
              onMouseEnter={(date: Date) => {
                setHoveredDate(date);
              }}
              onChange={onChange}
              rangeBackground={theme.palette.principal.grey30}
              value={getNextMonth(currentMonth)}
              onMonthChange={(date: Date) =>
                setCurrentMonth(new Date(date.getFullYear(), date.getMonth() - 1, 1))
              }
              onYearChange={(date: Date) =>
                setCurrentMonth(new Date(date.getFullYear(), date.getMonth() - 1, 1))
              }
            />
          </MuiBox>
        </Stack>
        <Stack
          minHeight={50}
          direction="row"
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          {hoveredDate && selectingRange && (
            <Stack
              direction="row"
              width="100%"
              alignItems="center"
              sx={{ px: 2, backgroundColor: '#FFF5CC' }}
            >
              <Typography variant="bodySingle" flexGrow={1}>
                Selecting{' '}
                {formatDateRangeString(
                  ...([selectingRange.start, hoveredDate].sort(
                    (a, b) => a.getTime() - b.getTime(),
                  ) as [Date, Date]),
                )}
              </Typography>
              <Button
                variant="text"
                onClick={() => {
                  setHoveredDate(null);
                  setSelectingRange(null);
                }}
              >
                Cancel
              </Button>
            </Stack>
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={cancelSelection}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={selectedRanges.length === 0}
            onClick={() => {
              void onSave?.(selectedRanges);
            }}
          >
            Save and close
          </Button>
        </Stack>
        {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
      </Stack>
    </Stack>
  );
};

type RangePillProps = {
  onRemove?: () => void;
  title: string;
  isSelecting?: boolean;
};

export const RangePill = ({ title, onRemove, isSelecting }: RangePillProps) => (
  <StyledChip
    label={title}
    onDelete={onRemove}
    deleteIcon={onRemove ? <Close /> : undefined}
    variant={'outlined'}
    color={isSelecting ? 'primary' : 'default'}
  />
);

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme }) => ({
  ...{
    backgroundColor: theme.palette.divider,
    color: theme.palette.common.black,
    fontWeight: theme.typography.captionPara.fontWeight,
    textTransform: 'uppercase',
    borderTopLeftRadius: '3px',
    borderBottomLeftRadius: '3px',
    borderTopRightRadius: '3px',
    borderBottomRightRadius: '3px',
    margin: '0.5em',
  },
}));

export const formatDateRangeString = (startDate: Date, endDate: Date): string => {
  const endDateString = format(endDate, 'dd MMM yyyy');
  if (isSameDay(startDate, endDate)) {
    return endDateString;
  }
  if (isSameMonth(startDate, endDate) && isSameYear(startDate, endDate)) {
    return `${format(startDate, 'dd')} - ${endDateString}`;
  }
  if (isSameYear(startDate, endDate)) {
    return `${format(startDate, 'dd MMM')} - ${endDateString}`;
  }
  return `${format(startDate, 'dd MMM yyyy')} - ${endDateString}`;
};
